import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import { useAuth } from '../../contexts/AuthContext';
import { SectionTitle } from '../performance/common';
import { ImportResult, PlatformRoute, PlatformRoutesApi, RouteSettings, STATUS_LABELS, platformRoutesApi } from '../../services/platformRoutesApi';
import { PlatformRouteDialog } from './PlatformRouteCard';

const idsText = (ids: string[]) => ids.join(', ');
const parseIds = (text: string) =>
  text
    .split(/[\s,]+/)
    .map(s => s.trim())
    .filter(Boolean);

const TestersDialog: React.FC<{ open: boolean; api: PlatformRoutesApi; onClose: () => void }> = ({ open, api, onClose }) => {
  const [settings, setSettings] = useState<{ testers: string; preview: string } | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setSettings(null);
    setError(null);
    api
      .settings()
      .then((s: RouteSettings) => setSettings({ testers: idsText(s.testers), preview: idsText(s.preview_accounts) }))
      .catch(e => setError(e instanceof Error ? e.message : 'Failed to load'));
  }, [api, open]);

  const save = async () => {
    if (!settings) return;
    setBusy(true);
    setError(null);
    try {
      await api.saveSettings({ testers: parseIds(settings.testers), preview_accounts: parseIds(settings.preview) });
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to save');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onClose={busy ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Testers</DialogTitle>
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        {!settings ? (
          <CircularProgress size={22} />
        ) : (
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="Passio customer ids"
              size="small"
              multiline
              minRows={2}
              value={settings.testers}
              onChange={e => setSettings({ ...settings, testers: e.target.value })}
              helperText="May call every Testing route through the platform API. Add the demo site's Passio customer id so its admins can try Testing routes."
            />
            <TextField
              label="EasyAI account ids that see Testing routes"
              size="small"
              multiline
              minRows={2}
              value={settings.preview}
              onChange={e => setSettings({ ...settings, preview: e.target.value })}
              helperText="Accounts whose API key lists Testing routes in the registry (e.g. the demo site's EasyAI account). The platform account and admins always do."
            />
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy}>
          Cancel
        </Button>
        <Button variant="contained" onClick={save} disabled={busy || !settings}>
          {busy ? <CircularProgress size={18} color="inherit" /> : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const ImportDialog: React.FC<{ open: boolean; api: PlatformRoutesApi; onClose: () => void; onImported: (r: ImportResult) => void }> = ({
  open,
  api,
  onClose,
  onImported,
}) => {
  const fileRef = useRef<HTMLInputElement>(null);
  const [bundle, setBundle] = useState<Record<string, unknown> | null>(null);
  const [fileName, setFileName] = useState('');
  const [status, setStatus] = useState<'off' | 'testing'>('off');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setBundle(null);
    setFileName('');
    setStatus('off');
    setError(null);
  }, [open]);

  const pick = async (file: File | undefined) => {
    if (!file) return;
    setError(null);
    try {
      const parsed = JSON.parse(await file.text());
      setBundle(parsed);
      setFileName(file.name);
    } catch {
      setError('That file isn\u2019t valid JSON');
    }
  };

  const run = async () => {
    if (!bundle) return;
    setBusy(true);
    setError(null);
    try {
      onImported(await api.importBundle(bundle, status));
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Import failed');
    } finally {
      setBusy(false);
    }
  };

  const route = (bundle?.route || {}) as { slug?: string; kind?: string };
  const fn = (bundle?.function || {}) as { name?: string };
  const tests = Array.isArray(bundle?.tests) ? bundle?.tests.length : 0;
  return (
    <Dialog open={open} onClose={busy ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Import a platform route</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Use a bundle exported from another EasyAI environment (e.g. dev). It creates or updates the function (a new version when one with the same name exists),
          its response objects, reliability and learning settings, the route and its test cases. Testers aren&apos;t copied.
        </Typography>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        <input ref={fileRef} type="file" accept="application/json,.json" hidden onChange={e => pick(e.target.files?.[0])} />
        <Button variant="outlined" onClick={() => fileRef.current?.click()} disabled={busy}>
          {fileName || 'Choose bundle file'}
        </Button>
        {bundle && (
          <Typography variant="body2" sx={{ mt: 1.5 }}>
            /napi/tools/{route.slug || '?'} · {fn.name || '?'} · {route.kind || '?'} kind · {tests} test case{tests === 1 ? '' : 's'}
          </Typography>
        )}
        <Box sx={{ mt: 2 }}>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
            Start as
          </Typography>
          <ToggleButtonGroup size="small" exclusive value={status} onChange={(_, v) => v && setStatus(v)}>
            <ToggleButton value="off">Off</ToggleButton>
            <ToggleButton value="testing">Testing</ToggleButton>
          </ToggleButtonGroup>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
            Switch to Live after checking it here.
          </Typography>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy}>
          Cancel
        </Button>
        <Button variant="contained" onClick={run} disabled={busy || !bundle}>
          {busy ? <CircularProgress size={18} color="inherit" /> : 'Import'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export const StatusChip: React.FC<{ status: PlatformRoute['status'] }> = ({ status }) => (
  <Chip size="small" variant="outlined" label={STATUS_LABELS[status].label} color={STATUS_LABELS[status].color} sx={{ height: 18, fontSize: 11 }} />
);

/** Platform Routes: functions served at /napi/tools/{slug} without a platform release. */
export const DynamicRoutesSection: React.FC<{ reload: number; onChanged: () => void }> = ({ reload, onChanged }) => {
  const { getIdToken } = useAuth();
  const navigate = useNavigate();
  const api = useMemo(() => platformRoutesApi(getIdToken), [getIdToken]);
  const [routes, setRoutes] = useState<PlatformRoute[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [open, setOpen] = useState<PlatformRoute | null>(null);
  const [testersOpen, setTestersOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const [changed, setChanged] = useState(0);

  useEffect(() => {
    let cancelled = false;
    api
      .routes()
      .then(d => !cancelled && setRoutes(d.routes))
      .catch(e => !cancelled && setError(e instanceof Error ? e.message : 'Failed to load'));
    return () => {
      cancelled = true;
    };
  }, [api, reload, changed]);

  const refresh = () => {
    setChanged(c => c + 1);
    onChanged();
  };

  return (
    <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
      <SectionTitle
        title="Dynamic routes"
        help="Platform functions the Passio platform serves at /napi/tools/<name> with no platform release. Off: not served. Testing: tester customers only. Live: everyone, and in the API docs. Each one is also listed below with its traffic and tests."
      />
      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 1.5 }}>
        <Button size="small" variant="contained" onClick={() => navigate('/dashboard/ai-functions?platform=1')}>
          New platform route
        </Button>
        <Button size="small" variant="outlined" onClick={() => setImportOpen(true)}>
          Import
        </Button>
        <Button size="small" variant="outlined" onClick={() => setTestersOpen(true)}>
          Testers
        </Button>
        <Typography variant="caption" color="text.secondary" sx={{ alignSelf: 'center' }}>
          Turn an existing function into a route from its Performance page (Platform function settings).
        </Typography>
      </Box>
      {notice && (
        <Alert severity="success" onClose={() => setNotice(null)} sx={{ mb: 1.5 }}>
          {notice}
        </Alert>
      )}
      {error && <Alert severity="error">{error}</Alert>}
      {!routes ? (
        !error && <CircularProgress size={22} />
      ) : routes.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          No dynamic routes yet.
        </Typography>
      ) : (
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Route</TableCell>
              <TableCell>Function</TableCell>
              <TableCell>Kind</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Updated</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {routes.map(r => (
              <TableRow key={r.function_id} hover sx={{ cursor: 'pointer' }} onClick={() => setOpen(r)}>
                <TableCell sx={{ fontFamily: 'monospace' }}>{r.path}</TableCell>
                <TableCell>{r.function_name}</TableCell>
                <TableCell>{r.kind}</TableCell>
                <TableCell>
                  <StatusChip status={r.status} />
                </TableCell>
                <TableCell>
                  <Typography variant="caption" color="text.secondary">
                    {r.updated_at ? new Date(r.updated_at).toLocaleString() : ''} {r.updated_by ? `· ${r.updated_by}` : ''}
                  </Typography>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
      <PlatformRouteDialog
        open={!!open}
        functionId={open?.function_id ?? null}
        title={open ? `Platform route: ${open.function_name}` : undefined}
        onClose={() => setOpen(null)}
        onChanged={refresh}
      />
      <TestersDialog open={testersOpen} api={api} onClose={() => setTestersOpen(false)} />
      <ImportDialog
        open={importOpen}
        api={api}
        onClose={() => setImportOpen(false)}
        onImported={r => {
          setImportOpen(false);
          const extra = [
            r.function_created ? 'new function' : 'new function version',
            r.objects.length ? `${r.objects.join(', ')}` : '',
            `${r.test_links} test case${r.test_links === 1 ? '' : 's'} linked`,
            r.skipped.length ? `skipped: ${r.skipped.join('; ')}` : '',
          ]
            .filter(Boolean)
            .join(' · ');
          setNotice(`Imported ${r.route.path} (${STATUS_LABELS[r.route.status].label}): ${extra}`);
          refresh();
        }}
      />
    </Paper>
  );
};

export default DynamicRoutesSection;
